import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, AlertCircle, AlertOctagon, Info, Eye, CheckCircle2,
  Clock, Search, Bell,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

type Alerta = {
  id: string;
  tipo: string;
  modulo: string;
  severidad: 'info'|'media'|'alta'|'critica';
  entidad: string;
  entidad_id: string;
  clave: string;
  titulo: string;
  mensaje: string;
  datos?: any;
  vence_en?: string;
  estado: 'activa'|'vista'|'resuelta'|'silenciada'|'expirada';
  vista_en?: string;
  resuelta_en?: string;
  created_at: string;
  updated_at: string;
};

export function AlertasPage() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<'activa'|'vista'|'resuelta'|'todas'>('activa');
  const [severidad, setSeveridad] = useState('');
  const [modulo, setModulo] = useState('');

  const { data: alertas } = useQuery({
    queryKey: ['alertas', estado, severidad, modulo],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado !== 'todas') p.set('estado', estado);
      if (severidad) p.set('severidad', severidad);
      if (modulo) p.set('modulo', modulo);
      p.set('limit', '500');
      return api<Alerta[]>(`/alertas?${p}`);
    },
  });
  const { data: contador } = useQuery({
    queryKey: ['alertas-contador'],
    queryFn: () => api<Record<string, number>>('/alertas/contador'),
    refetchInterval: 60000,
  });

  async function marcarVista(id: string) {
    await api(`/alertas/${id}/vista`, { method: 'PATCH' });
    qc.invalidateQueries({ queryKey: ['alertas'] });
    qc.invalidateQueries({ queryKey: ['alertas-contador'] });
  }
  async function resolver(id: string) {
    await api(`/alertas/${id}/resuelta`, { method: 'PATCH' });
    qc.invalidateQueries({ queryKey: ['alertas'] });
    qc.invalidateQueries({ queryKey: ['alertas-contador'] });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Motor de alertas transversal</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Alertas del sistema</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Compila eventos críticos de 9 fuentes (almacén, bienes, proveedores, CxP, presupuesto) y se actualiza cada 15 minutos.
        </p>
      </div>

      <section className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Pill label="Total activas" value={(contador?.total ?? 0).toString()} icon={<Bell size={16} />} color="brand" />
        <Pill label="Críticas"       value={(contador?.critica ?? 0).toString()} icon={<AlertOctagon size={16} />} color="danger" />
        <Pill label="Alta"           value={(contador?.alta ?? 0).toString()}    icon={<AlertTriangle size={16} />} color="warn" />
        <Pill label="Media"          value={(contador?.media ?? 0).toString()}   icon={<AlertCircle size={16} />} color="sky" />
        <Pill label="Info"           value={(contador?.info ?? 0).toString()}    icon={<Info size={16} />} color="neutral" />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium">
          {(['activa','vista','resuelta','todas'] as const).map((e) => (
            <button key={e} onClick={() => setEstado(e)}
              className={cn('rounded px-3 py-1.5 capitalize transition-colors',
                estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
              {e}
            </button>
          ))}
        </div>
        <select value={severidad} onChange={(e) => setSeveridad(e.target.value)}
          className="h-9 rounded-md border border-border bg-surface px-3 text-[13px]">
          <option value="">Todas severidades</option>
          <option value="critica">Crítica</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="info">Info</option>
        </select>
        <select value={modulo} onChange={(e) => setModulo(e.target.value)}
          className="h-9 rounded-md border border-border bg-surface px-3 text-[13px]">
          <option value="">Todos módulos</option>
          <option value="almacen">Almacén</option>
          <option value="bienes">Bienes</option>
          <option value="proveedores">Proveedores</option>
          <option value="cxp">CxP</option>
          <option value="presupuesto">Presupuesto</option>
        </select>
      </div>

      <Card>
        <CardBody className="p-0">
          {!alertas || alertas.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <CheckCircle2 size={32} className="text-emerald-500" />
              <p className="text-[14px] font-medium text-ink">Sin alertas</p>
              <p className="text-[12px] text-ink-mute">
                {estado === 'activa' ? 'Todo está en orden — no hay alertas activas.' : 'No hay alertas que coincidan con el filtro.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {alertas.map((a) => (
                <li key={a.id} className="flex items-start gap-4 px-5 py-4 transition-colors hover:bg-muted/40">
                  <SeveridadIcon s={a.severidad} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-[14px] font-semibold text-ink">{a.titulo}</h3>
                        <p className="mt-0.5 text-[12px] text-ink-mute">{a.mensaje}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <ModuloBadge m={a.modulo} />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-soft">
                      <span className="num">{a.clave}</span>
                      <span>·</span>
                      <span>Detectada {fmt.datetime(a.created_at)}</span>
                      {a.vence_en && <>
                        <span>·</span>
                        <span>Vence {fmt.date(a.vence_en)}</span>
                      </>}
                      {a.estado !== 'activa' && <EstadoPill e={a.estado} />}
                    </div>
                  </div>
                  {a.estado === 'activa' && (
                    <div className="flex gap-1">
                      <button onClick={() => marcarVista(a.id)}
                        className="rounded-md p-1.5 text-ink-mute hover:bg-muted hover:text-ink"
                        title="Marcar vista">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => resolver(a.id)}
                        className="rounded-md p-1.5 text-ink-mute hover:bg-muted hover:text-emerald-600"
                        title="Marcar resuelta">
                        <CheckCircle2 size={14} />
                      </button>
                    </div>
                  )}
                  {a.estado === 'vista' && (
                    <Button size="sm" variant="ghost" onClick={() => resolver(a.id)}>Resolver</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function SeveridadIcon({ s }: { s: Alerta['severidad'] }) {
  const cfg = {
    critica: { icon: AlertOctagon, color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/40' },
    alta:    { icon: AlertTriangle, color: 'text-amber-700 bg-amber-50 dark:bg-amber-900/40' },
    media:   { icon: AlertCircle, color: 'text-sky-700 bg-sky-50 dark:bg-sky-900/40' },
    info:    { icon: Info, color: 'text-ink-mute bg-stone-100 dark:bg-white/5' },
  }[s];
  const Icon = cfg.icon;
  return <div className={cn('flex h-8 w-8 items-center justify-center rounded-md shrink-0', cfg.color)}><Icon size={14} /></div>;
}

function ModuloBadge({ m }: { m: string }) {
  return <span className="rounded border border-border bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-mute">{m}</span>;
}

function EstadoPill({ e }: { e: string }) {
  const cfg = {
    vista: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    resuelta: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    silenciada: 'bg-stone-100 text-ink-mute dark:bg-white/5',
    expirada: 'bg-stone-100 text-ink-soft dark:bg-white/5',
  } as Record<string, string>;
  return <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', cfg[e] ?? 'bg-muted text-ink-mute')}>{e}</span>;
}

function Pill({ label, value, icon, color }: {
  label: string; value: string; icon: React.ReactNode;
  color: 'brand'|'danger'|'warn'|'sky'|'neutral';
}) {
  const cfg = {
    brand: 'before:bg-brand-500',
    danger: 'before:bg-rose-500',
    warn: 'before:bg-amber-500',
    sky: 'before:bg-sky-500',
    neutral: 'before:bg-ink-soft',
  }[color];
  return (
    <div className={cn(
      'relative overflow-hidden rounded-lg border border-border bg-surface p-4 shadow-card',
      'before:absolute before:inset-x-0 before:top-0 before:h-[3px]', cfg,
    )}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-mute">{label}</span>
        <span className="text-ink-soft">{icon}</span>
      </div>
      <div className="num mt-2 text-[24px] font-semibold text-ink">{value}</div>
    </div>
  );
}

// @ts-expect-error
const _ = [Clock, Search];
